import { useRef, useState } from 'react';
import { FADelete, FAEye } from '../../../components/atoms/fontawesomeIcon/Fontawesome';
import { MAX_POINTS, MIN_POINTS, PickedQuestion, questionKey } from './types';

/**
 * The six dots a row is moved by - the same handle the curriculum's skill list uses, so a teacher
 * who has reordered skills there recognises it here without being told.
 */
const GripDots = () => (
  <svg width="14" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {[5, 12, 19].map((y) =>
      [9, 15].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill="currentColor" />),
    )}
  </svg>
);

/**
 * The quiz as it stands: the questions the teacher has moved into it, in order.
 *
 * Once a question is moved it leaves the list on the left, so this panel is where it is managed -
 * what it reads as, which skill it came from, what it is worth, a look at it, where it sits in the
 * quiz, and taking it out again.
 */
export function SelectedQuestions(props: {
  picked: PickedQuestion[];
  totalPoints: number;
  onPoints: (key: string, points: number) => void;
  onView: (key: string) => void;
  onRemove: (key: string) => void;
  /** Ask to empty the quiz - the builder confirms it before anything is removed. */
  onRemoveAll: () => void;
  onResetPoints: () => void;
  onSave: () => void;
  /** Editing a quiz that already exists, rather than building a new one. Only the wording of the
   *  save button changes - a teacher who opened an existing quiz is updating it, not saving a new
   *  one, and the button should say so. */
  isEdit?: boolean;
  /** Move a question to another place in the quiz - students get them in this order. */
  onReorder: (from: number, to: number) => void;
  /** Give this panel most of the screen, leaving the questions a single column. */
  wide?: boolean;
  onToggleWide?: () => void;
}) {
  /** The row being moved, and the one it is over. Index, not key: it is a position. */
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  /** How far the held row has been carried, and how far the rows it passes have to step aside. */
  const [carriedBy, setCarriedBy] = useState(0);
  const [rowStep, setRowStep] = useState(0);
  const grabbedAt = useRef(0);
  const listRef = useRef<HTMLOListElement | null>(null);

  /**
   * Moving a question works by POINTER, not by HTML5 drag-and-drop.
   *
   * Drag-and-drop never fires from a finger - not on Android Chrome, not on iOS Safari - so on the
   * tablet a teacher builds a quiz on, the rows simply would not move. Pointer events are the same
   * code for a mouse, a finger and a pen.
   *
   * It starts from the grip alone, and only the grip refuses the browser's own touch gestures
   * (`touch-action: none`): a swipe anywhere else still scrolls the list, which is what a teacher
   * expects when they have twenty questions in front of them.
   */
  const rowUnder = (x: number, y: number): number | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const row = el?.closest?.('li[data-index]') as HTMLElement | null;
    if (!row || !listRef.current?.contains(row)) return null;
    const index = Number(row.getAttribute('data-index'));
    return Number.isFinite(index) ? index : null;
  };

  const startMove = (index: number) => (event: React.PointerEvent) => {
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    // How far every row this one passes has to step aside: the height this row takes OUT of the
    // list, which is its own height plus the space between rows - the same whatever the neighbours
    // measure, so one number moves them all.
    const row = (event.currentTarget as HTMLElement).closest('li');
    const gap = listRef.current ? parseFloat(getComputedStyle(listRef.current).rowGap) || 0 : 0;
    setRowStep(row ? row.getBoundingClientRect().height + gap : 0);
    grabbedAt.current = event.clientY;
    setCarriedBy(0);
    setDragFrom(index);
    setDragOver(index);
  };

  const moveOver = (event: React.PointerEvent) => {
    if (dragFrom === null) return;
    event.preventDefault();
    // The held row goes with the finger, so the teacher is moving the question itself rather than
    // watching a line move somewhere else on the screen.
    setCarriedBy(event.clientY - grabbedAt.current);
    const over = rowUnder(event.clientX, event.clientY);
    if (over !== null && over !== dragOver) setDragOver(over);
  };

  const endMove = () => {
    if (dragFrom !== null && dragOver !== null && dragFrom !== dragOver) {
      props.onReorder(dragFrom, dragOver);
    }
    setDragFrom(null);
    setDragOver(null);
    setCarriedBy(0);
  };

  /**
   * Where a row is drawn while another one is being carried over it.
   *
   * The held row follows the pointer exactly, with no transition on it - anything else lags behind
   * the finger. The rows it has passed slide the other way by one row's worth, and THEY are the ones
   * that animate: the gap opening where the question will land is the whole point of the movement,
   * and it is what tells a teacher what dropping here would do.
   */
  const rowStyle = (index: number): React.CSSProperties => {
    if (dragFrom === null || dragOver === null) return {};
    if (index === dragFrom) return { transform: `translateY(${carriedBy}px)` };
    if (dragFrom < dragOver && index > dragFrom && index <= dragOver) {
      return { transform: `translateY(${-rowStep}px)` };
    }
    if (dragFrom > dragOver && index >= dragOver && index < dragFrom) {
      return { transform: `translateY(${rowStep}px)` };
    }
    return {};
  };

  /** And by keyboard, for anyone not using a pointer at all. */
  const moveByKey = (index: number) => (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault();
      props.onReorder(index, index - 1);
    } else if (event.key === 'ArrowDown' && index < props.picked.length - 1) {
      event.preventDefault();
      props.onReorder(index, index + 1);
    }
  };

  return (
    <aside className={`aq-selected${props.wide ? ' aq-selected-wide' : ''}`}>
      <div className="aq-selected-head">
        <h2>Selected ({props.picked.length})</h2>
        {/* More room for the question text, at the cost of the questions being one to a row. The
            questions themselves are not re-rendered by this - the row they sit in simply narrows. */}
        {props.onToggleWide ? (
          <button
            type="button"
            className="aq-icon-btn aq-wide-toggle"
            onClick={props.onToggleWide}
            title={props.wide ? 'Give the questions the room back' : 'Give this panel more room'}
            aria-label={props.wide ? 'Give the questions the room back' : 'Give this panel more room'}
            aria-pressed={Boolean(props.wide)}
          >
            {props.wide ? '⤡' : '⤢'}
          </button>
        ) : null}
      </div>
      {/* Label first here too, so a quiz worth one point does not read as "1 points in total". */}
      <p className="aq-total">Total points: {props.totalPoints}</p>

      {props.picked.length === 0 ? (
        <p className="aq-notice">
          Nothing chosen yet. Tick the questions you want and move them across.
        </p>
      ) : (
        <ol ref={listRef}>
          {props.picked.map((question, index) => {
            const key = questionKey(question);
            const name = question.label || question.skillName || question.type;
            return (
              <li
                key={key}
                data-index={index}
                style={rowStyle(index)}
                className={[
                  'aq-row',
                  dragFrom === index ? 'aq-row-carried' : '',
                  dragFrom !== null && index !== dragFrom ? 'aq-row-giving-way' : '',
                ]
                  .join(' ')
                  .trim()}
              >
                {/* Take hold of the dots to move the question; the number beside them says where
                    it currently sits, which is what moving it changes. */}
                <button
                  type="button"
                  className="aq-grip"
                  title="Move this question"
                  aria-label={`Move question ${index + 1}. Use the arrow keys.`}
                  onPointerDown={startMove(index)}
                  onPointerMove={moveOver}
                  onPointerUp={endMove}
                  onPointerCancel={endMove}
                  onKeyDown={moveByKey(index)}
                >
                  <GripDots />
                </button>
                <span className="aq-selected-order" aria-hidden="true">
                  {index + 1}
                </span>

                <span className="aq-selected-text">
                  {/* How the question starts: a teacher has to recognise it here, where the question
                      itself is no longer on screen. */}
                  <span className="aq-selected-name" title={name}>
                    {name}
                  </span>
                  {/* And which skill it came from - several skills end up in one quiz. */}
                  {question.skillName ? (
                    <span className="aq-selected-skill" title={question.skillName}>
                      {question.skillName}
                    </span>
                  ) : null}
                </span>

                {/* Points are edited here - once moved, this is the only place. */}
                <span className="aq-points aq-selected-points">
                  <button
                    type="button"
                    aria-label="Fewer points"
                    disabled={question.points <= MIN_POINTS}
                    onClick={() => props.onPoints(key, question.points - 1)}
                  >
                    −
                  </button>
                  <strong>{question.points}</strong>
                  <button
                    type="button"
                    aria-label="More points"
                    disabled={question.points >= MAX_POINTS}
                    onClick={() => props.onPoints(key, question.points + 1)}
                  >
                    +
                  </button>
                </span>

                <span className="aq-selected-actions">
                  <button
                    type="button"
                    className="aq-icon-btn"
                    title="View this question"
                    aria-label="View this question"
                    onClick={() => props.onView(key)}
                  >
                    <FAEye color="#475569" />
                  </button>
                  <button
                    type="button"
                    className="aq-icon-btn aq-icon-danger"
                    title="Remove from this quiz"
                    aria-label="Remove from this quiz"
                    onClick={() => props.onRemove(key)}
                  >
                    <FADelete color="#e11d48" />
                  </button>
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {/* The three things that can be done to the quiz as a whole. They are one under the other in
          a narrow panel and side by side once it is given the room, which is the one place there is
          width to spare. */}
      <div className="aq-selected-buttons">
        {/* Asked before it happens, in a dialog of its own - every other button in this panel undoes
            one thing, and this one undoes an afternoon of choosing. */}
        <button
          type="button"
          className="aq-btn aq-btn-danger"
          disabled={props.picked.length === 0}
          onClick={props.onRemoveAll}
        >
          Remove all questions
        </button>

        <button
          type="button"
          className="aq-btn"
          disabled={props.picked.length === 0}
          onClick={props.onResetPoints}
        >
          Reset points to defaults
        </button>

        <button
          type="button"
          className="aq-btn aq-btn-primary"
          disabled={props.picked.length === 0}
          onClick={props.onSave}
        >
          {props.isEdit ? 'Update quiz' : 'Save quiz'}
        </button>
      </div>
    </aside>
  );
}

export default SelectedQuestions;
